<template>
  <TTabGroup
    :default-index="defaultIndex"
    :selected-index="selectedIndex"
    @change="handleChange"
  >
    <div class="flex h-full flex-col">
      <div>
        <TTabList>
          <TTab
            v-for="(tab, index) in tabs"
            :key="tab.key ?? index"
            :label="tab.label"
            :icon="tab.icon"
            :disabled="tab.disabled"
          >
            <!-- Allow custom tab content via slots -->
            <template v-if="$slots[`tab-${index}`]" #default="{ selected }">
              <slot :name="`tab-${index}`" :tab="tab" :selected="selected" />
            </template>
            <template v-if="$slots[`tab-badge-${index}`]" #badge="{ selected }">
              <slot
                :name="`tab-badge-${index}`"
                :tab="tab"
                :selected="selected"
              />
            </template>
          </TTab>
          <template #after>
            <slot name="after" />
          </template>
        </TTabList>
      </div>
      <div class="grow overflow-auto">
        <TTabPanels>
          <TTabPanel v-for="(tab, index) in tabs" :key="tab.key ?? index">
            <slot :name="`panel-${index}`" :tab="tab" />
          </TTabPanel>
        </TTabPanels>
      </div>
    </div>
  </TTabGroup>
</template>

<script setup lang="ts">
import TTabGroup from './TTabGroup.vue';
import TTabList from './TTabList.vue';
import TTab from './TTab.vue';
import TTabPanels from './TTabPanels.vue';
import TTabPanel from './TTabPanel.vue';
import type { IconType } from '../Icon/icon';

export type TabItem = {
  /** Unique key for the tab */
  key?: string;
  /** Display label for the tab */
  label: string;
  /** Icon name to display */
  icon?: IconType;
  /** Whether the tab is disabled */
  disabled?: boolean;
};

type Props = {
  /** Array of tab configurations */
  tabs: TabItem[];
  /** The default tab index to show initially */
  defaultIndex?: number;
  /** Controlled selected index for external control */
  selectedIndex?: number;
};

type Emits = {
  /** Emitted when the selected tab changes */
  change: [index: number];
};

defineProps<Props>();
const emit = defineEmits<Emits>();

function handleChange(index: number): void {
  emit('change', index);
}
</script>
